import { Input, Button, Switch, Spin, Empty, Popconfirm, Tag } from 'antd';
import {
    SearchOutlined,
    PlusOutlined,
    MessageOutlined,
    PlayCircleOutlined,
    CommentOutlined,
    DeleteOutlined,
    BarChartOutlined
} from '@ant-design/icons';

export default function AutomationsList({
    automations = [],
    loading = false,
    searchQuery = '',
    onSearchChange,
    onCreateNew,
    onSelect,
    onToggle,
    onDelete,
    onViewInsights
}) {
    const activeCount = automations.filter(a => a.live).length;

    const getTriggerIcon = (type) => {
        switch (type) {
            case 'dm_reply': return <MessageOutlined />;
            case 'story_reply': return <PlayCircleOutlined />;
            case 'comment_reply': return <CommentOutlined />;
            default: return <MessageOutlined />;
        }
    };

    const getTriggerType = (automation) => {
        const autoReply = automation?.nodes?.[0]?.data?.auto_reply;
        if (autoReply?.dm_reply?.enabled) return 'dm_reply';
        if (autoReply?.story_reply?.enabled) return 'story_reply';
        if (autoReply?.comment_reply?.enabled) return 'comment_reply';
        return 'dm_reply';
    };

    const getKeywords = (automation) => {
        const autoReply = automation?.nodes?.[0]?.data?.auto_reply;
        const triggerType = getTriggerType(automation);
        return autoReply?.[triggerType]?.triger_words || [];
    };

    const getPreviewText = (automation) => {
        // Find first message node
        const messageNode = automation?.nodes?.find(n => n.type === 'messageReply');
        const message = messageNode?.data?.messages?.[0];
        if (message?.text) return message.text;
        if (message?.templates?.[0]?.title) return message.templates[0].title;
        return 'No message configured';
    };

    if (loading && automations.length === 0) {
        return (
            <div className="automations-list" style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="automations-list">
            {/* Header */}
            <div className="automations-header">
                <div>
                    <h1>
                        Quick DM Automations <Tag>Beta</Tag>
                    </h1>
                    <p style={{ color: '#6b7280', marginTop: '8px', fontSize: '14px', maxWidth: '800px', lineHeight: '1.5' }}>
                        Use for Instagram DM automation: You can collect emails, tag contacts, and respond to messages. If you want to build more complex workflows with follow-ups and delays, use our Flow Editor.
                    </p>
                </div>
                <div className="header-actions">
                    <Input
                        placeholder="Search by keyword or response"
                        prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        className="search-input"
                        allowClear
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={onCreateNew}
                        size="large"
                        style={{ height: '40px', borderRadius: '8px', fontWeight: 500 }}
                    >
                        Create New
                    </Button>
                </div>
            </div>


            {/* Automation Cards */}
            {automations.length === 0 ? (
                <Empty
                    description="No automations yet"
                    style={{ padding: '60px 0' }}
                >
                    <Button type="primary" onClick={onCreateNew}>
                        Create your first Quick DM Automation
                    </Button>
                </Empty>
            ) : (
                automations.map((automation) => {
                    const triggerType = getTriggerType(automation);
                    const keywords = getKeywords(automation);
                    const previewText = getPreviewText(automation);

                    return (
                        <div
                            key={automation.id}
                            className="automation-card"
                            onClick={() => onSelect(automation)}
                        >
                            <div className="automation-card-header">
                                <div className={`automation-card-icon ${triggerType.replace('_reply', '')}`}>
                                    {getTriggerIcon(triggerType)}
                                </div>
                                <div className="automation-card-title">
                                    <h3>
                                        {automation?.nodes?.[0]?.data?.auto_reply?.[triggerType]?.automate_all === 'true' ? (
                                            <>
                                                <span style={{ color: '#888', fontWeight: 400 }}>TRIGGER</span>
                                                {' '}
                                                <span className="keyword-tag" style={{ background: '#fce7f3', color: '#db2777' }}>
                                                    "Any Message"
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <span style={{ color: '#888', fontWeight: 400 }}>KEYWORD</span>
                                                {' '}
                                                <span className="keyword-tag">
                                                    "{keywords[0] || 'Any'}"
                                                </span>
                                            </>
                                        )}
                                    </h3>
                                    <p>{previewText}</p>
                                </div>
                            </div>

                            <div className="automation-card-footer">

                                <div className="automation-badges">
                                    {automation.live && (
                                        <Button
                                            size="small"
                                            type="default"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onViewInsights(automation);
                                            }}
                                            style={{ fontSize: 12, marginRight: 8 }}
                                        >
                                            Contacts
                                        </Button>
                                    )}

                                    {!automation.live && (
                                        <span className="badge inactive">INACTIVE</span>
                                    )}
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <Switch
                                            checked={automation.live}
                                            onChange={(checked) => {
                                                onToggle(automation.id, checked);
                                            }}
                                        />
                                    </div>
                                    <Popconfirm
                                        title="Delete this automation?"
                                        onConfirm={(e) => {
                                            e?.stopPropagation();
                                            onDelete(automation.id);
                                        }}
                                        onCancel={(e) => e?.stopPropagation()}
                                        okText="Delete"
                                        cancelText="Cancel"
                                    >
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </Popconfirm>
                                </div>
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );
}
